/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
// FLAGGED-FOR-REVIEW-2026-02: This file has residual unused vars / state
// setters / effect-deps that look like DROPPED-FEATURE references, not
// blind-orphan code (state setters never called, computed values never
// rendered, hooks with intentional dep omissions). Reviewer next step:
// walk each warning and decide either RESTORE the callsite that references
// the var, OR delete the var. Do NOT bulk-delete without review — several
// of these signal features that were half-migrated.
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  AlertTriangle, CheckCircle2, HelpCircle, Plus, X, Filter
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { formatSceneNumber } from '../utils/sceneNumber';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { LightboxThumb } from '../components/ImageUpload';
import { AuthImage } from '../components/AuthImage';
import { toast } from 'sonner';
import { MLText } from '../components/MLText';
import { getBreakdownItemLabel } from '../utils/breakdownLabel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Cell flag config ──
const FLAG_CONFIG = {
  ok:      { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', icon: CheckCircle2, iconColor: 'text-emerald-400', label: 'Same look' },
  changed: { bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   icon: AlertTriangle, iconColor: 'text-amber-400',   label: 'Costume changed' },
  missing: { bg: 'bg-red-500/10',     border: 'border-red-500/30',     icon: HelpCircle,   iconColor: 'text-red-400',     label: 'No costume assigned' },
};

const LOC_COLORS = {
  'INT DAY':   'bg-amber-500/20 text-amber-300',
  'INT NIGHT': 'bg-indigo-500/20 text-indigo-300',
  'EXT DAY':   'bg-sky-500/20 text-sky-300',
  'EXT NIGHT': 'bg-violet-500/20 text-violet-300',
};
const locPill = (s) => LOC_COLORS[`${s.location_type} ${s.time_of_day}`] || 'bg-[#2a2a2a] text-[#a0a0a0]';

// ── Cell component ──
const ContinuityCell = ({ cell, onAdd, token, compact }) => {
  const [hovered, setHovered] = useState(false);

  if (!cell || !cell.in_scene) {
    return (
      <td className="border border-[#1a1a1a] bg-[#0a0a0a] relative">
        <div className="w-full h-full flex items-center justify-center opacity-10">
          <div className="w-full h-full" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2a2a2a 0, #2a2a2a 1px, transparent 0, transparent 50%)', backgroundSize: '6px 6px' }} />
        </div>
      </td>
    );
  }

  const cfg = FLAG_CONFIG[cell.flag] || FLAG_CONFIG.ok;
  const Icon = cfg.icon;
  const firstCostume = cell.costumes?.[0];

  return (
    <td
      className={`border border-[#1a1a1a] ${cfg.bg} ${cfg.border} relative cursor-pointer transition-all`}
      style={{ minWidth: compact ? '120px' : '160px', maxWidth: compact ? '120px' : '160px', height: compact ? '64px' : '80px' }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onAdd}
      data-testid={`cell-${cell.flag}`}
    >
      <div className="p-1.5 h-full flex flex-col justify-between">
        <div className="flex items-start justify-between gap-1">
          <Icon className={`h-3 w-3 shrink-0 mt-0.5 ${cfg.iconColor}`} />
          {cell.costumes.length > 0 && (
            <span className="text-[9px] text-[#555]">{cell.costumes.length}</span>
          )}
        </div>

        {firstCostume ? (
          <div className="flex items-center gap-1.5 overflow-hidden">
            {firstCostume.image_paths?.length > 0 ? (
              <AuthImage
                path={firstCostume.image_paths[0]}
                alt=""
                className="h-7 w-7 rounded object-cover shrink-0 border border-[#2a2a2a]"
              />
            ) : (
              <div className="h-7 w-7 rounded bg-[#2a2a2a] flex items-center justify-center shrink-0 text-[#444] text-xs">C</div>
            )}
            <div className="min-w-0 flex-1">
              <MLText as="p" className="text-[10px] text-[#f0f0f0] truncate leading-tight">{getBreakdownItemLabel(firstCostume.description, 'original')}</MLText>
              {firstCostume.source && <p className="text-[9px] text-[#555] truncate">{firstCostume.source}</p>}
            </div>
          </div>
        ) : (
          <div className={`text-[10px] ${cfg.iconColor} font-medium`}>{cfg.label}</div>
        )}
      </div>

      {/* Hover overlay */}
      {hovered && (
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center rounded-sm z-10">
          <div className="flex gap-1">
            <div className="bg-[#1a1a1a] text-[#f0f0f0] text-xs px-2 py-1 rounded border border-[#2a2a2a]">
              {cell.costumes.length > 0 ? 'Edit' : '+ Add'}
            </div>
          </div>
        </div>
      )}
    </td>
  );
};

// ── Character row header ──
const CharacterHeader = ({ char, summary, search }) => {
  const s = summary[char] || {};
  const highlight = search && char.toLowerCase().includes(search.toLowerCase());
  return (
    <td
      className={`sticky left-0 z-10 border border-[#2a2a2a] p-2 bg-[#0d0d0d] text-left min-w-[160px] max-w-[160px] ${highlight ? 'ring-2 ring-inset ring-[#e8c547]' : ''}`}
      data-testid={`char-header-${char}`}
    >
      <div className="flex items-center justify-between gap-1">
        <div className="min-w-0">
          <div className="text-xs font-semibold text-[#f0f0f0] truncate leading-tight" title={char}>{char}</div>
          <div className="text-[10px] text-[#555] mt-0.5">{s.total_scenes} scene{s.total_scenes !== 1 ? 's' : ''}</div>
        </div>
        <div className="flex flex-col gap-0.5 shrink-0">
          {s.missing > 0 && (
            <Badge className="bg-red-500/20 text-red-400 border-0 text-[9px] h-3.5 px-1" data-testid={`missing-badge-${char}`}>
              {s.missing} ✗
            </Badge>
          )}
          {s.changed > 0 && (
            <Badge className="bg-amber-500/20 text-amber-400 border-0 text-[9px] h-3.5 px-1" data-testid={`changed-badge-${char}`}>
              {s.changed} ⚠
            </Badge>
          )}
        </div>
      </div>
    </td>
  );
};

// ── Scene column header ──
const SceneHeader = ({ scene }) => (
  <th className="border border-[#2a2a2a] bg-[#0d0d0d] p-0 text-center" style={{ minWidth: '160px', maxWidth: '160px' }}>
    <div className="px-2 py-2">
      <div className="text-xs font-bold text-[#e8c547] mb-1">Sc. {formatSceneNumber(scene.scene_number)}</div>
      <div className={`text-[9px] px-1 py-0.5 rounded-full inline-block ${locPill(scene)}`}>
        {scene.location_type} {scene.time_of_day}
      </div>
      <div className="text-[9px] text-[#555] truncate mt-0.5" title={scene.location_name}>
        {scene.location_name?.length > 14 ? scene.location_name.slice(0, 14) + '…' : scene.location_name}
      </div>
    </div>
  </th>
);

// ── Main Page ──
export default function ContinuityBoardPage() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterFlag, setFilterFlag] = useState('all'); // all | missing | changed | ok
  const [compact, setCompact] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // {char, sceneId, sceneMeta, cell}
  const [allScenes, setAllScenes] = useState([]);
  const [newCostume, setNewCostume] = useState({ description: '', source: 'purchase', notes: '' });
  const [savingCostume, setSavingCostume] = useState(false);
  const tableRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => { fetchAll(); }, [projectId]);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [contRes, bdRes] = await Promise.all([
        axios.get(`${API}/projects/${projectId}/continuity`, { headers }),
        axios.get(`${API}/projects/${projectId}/breakdown`, { headers }),
      ]);
      setData(contRes.data);
      setAllScenes(bdRes.data);
    } catch { toast.error('Failed to load continuity data'); }
    finally { setLoading(false); }
  };

  // Summary stats
  const totalMissing = data ? Object.values(data.summary).reduce((s, v) => s + v.missing, 0) : 0;
  const totalChanged = data ? Object.values(data.summary).reduce((s, v) => s + v.changed, 0) : 0;
  const totalOk = data ? Object.values(data.summary).reduce((s, v) => s + v.total_scenes - v.missing, 0) : 0;

  // Filtered characters
  const filteredChars = data ? data.characters.filter(char => {
    if (search && !char.toLowerCase().includes(search.toLowerCase())) return false;
    if (filterFlag === 'all') return true;
    const s = data.summary[char] || {};
    if (filterFlag === 'missing') return s.missing > 0;
    if (filterFlag === 'changed') return s.changed > 0;
    if (filterFlag === 'ok') return s.missing === 0 && s.changed === 0;
    return true;
  }) : [];

  // Handle cell click → open dialog
  const handleCellClick = (char, sceneId) => {
    if (!data) return;
    const sceneMeta = data.scenes.find(s => s.id === sceneId);
    const cell = data.grid[char]?.[sceneId];
    setSelectedCell({ char, sceneId, sceneMeta, cell });
    setNewCostume({ description: '', source: 'purchase', notes: '' });
  };

  // Add costume for character in scene
  const handleAddCostume = async () => {
    if (!newCostume.description.trim()) { toast.error('Costume description required'); return; }
    if (!selectedCell) return;
    setSavingCostume(true);
    try {
      await axios.post(`${API}/projects/${projectId}/breakdown-items`, {
        scene_id: selectedCell.sceneId,
        category: 'COSTUME',
        description: newCostume.description,
        character_name: selectedCell.char,
        source: newCostume.source || undefined,
        notes: newCostume.notes || undefined,
      }, { headers });
      toast.success('Costume added');
      setSelectedCell(null);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    finally { setSavingCostume(false); }
  };

  const handleDeleteCostume = async (itemId) => {
    try {
      await axios.delete(`${API}/breakdown-items/${itemId}`, { headers });
      toast.success('Removed');
      fetchAll();
      setSelectedCell(null);
    } catch { toast.error('Failed to remove'); }
  };

  if (loading) return <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center text-[#a0a0a0]">Building continuity board...</div>;
  if (!data || data.characters.length === 0) return (
    <div className="min-h-screen bg-[#0d0d0d] flex items-center justify-center">
      <Card className="bg-[#1a1a1a] border-[#2a2a2a] p-12 text-center max-w-md">
        <AlertTriangle className="h-12 w-12 text-[#2a2a2a] mx-auto mb-3" />
        <p className="text-[#f0f0f0] font-semibold mb-2">No characters found</p>
        <p className="text-sm text-[#a0a0a0]">Upload and parse a script first to see the continuity board.</p>
      </Card>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0d0d0d] text-[#f0f0f0] flex flex-col">
      {/* Header */}
      <div className="border-b border-[#2a2a2a] px-6 py-4 shrink-0">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="font-['DM_Serif_Display'] text-2xl text-[#f0f0f0]">Continuity Board</h1>
            <p className="text-sm text-[#a0a0a0] mt-0.5">Track costume continuity across scenes. Click any cell to add or review costumes.</p>
          </div>
        </div>

        {/* Stats pills */}
        <div className="flex gap-3 mb-4 flex-wrap">
          <button onClick={() => setFilterFlag('all')} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-all ${filterFlag === 'all' ? 'bg-[#e8c547] text-black border-[#e8c547] font-medium' : 'border-[#2a2a2a] text-[#a0a0a0] hover:border-[#e8c547]/40'}`} data-testid="filter-all">
            All — {data.characters.length} characters
          </button>
          <button onClick={() => setFilterFlag('missing')} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-all ${filterFlag === 'missing' ? 'bg-red-500/20 text-red-300 border-red-500/50 font-medium' : 'border-[#2a2a2a] text-[#a0a0a0] hover:border-red-500/40'}`} data-testid="filter-missing">
            <HelpCircle className="h-3 w-3" /> Missing — {totalMissing} cells
          </button>
          <button onClick={() => setFilterFlag('changed')} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-all ${filterFlag === 'changed' ? 'bg-amber-500/20 text-amber-300 border-amber-500/50 font-medium' : 'border-[#2a2a2a] text-[#a0a0a0] hover:border-amber-500/40'}`} data-testid="filter-changed">
            <AlertTriangle className="h-3 w-3" /> Changed — {totalChanged} cells
          </button>
          <button onClick={() => setFilterFlag('ok')} className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs border transition-all ${filterFlag === 'ok' ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/50 font-medium' : 'border-[#2a2a2a] text-[#a0a0a0] hover:border-emerald-500/40'}`} data-testid="filter-ok">
            <CheckCircle2 className="h-3 w-3" /> No Issues
          </button>
        </div>

        {/* Controls */}
        <div className="flex gap-3 items-center flex-wrap">
          <div className="relative">
            <Filter className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#a0a0a0]" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search characters..."
              className="pl-8 bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] h-8 w-52 text-sm"
              data-testid="char-search"
            />
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={() => setCompact(!compact)}
            className={`border-[#2a2a2a] h-8 text-xs ${compact ? 'text-[#e8c547] border-[#e8c547]/40' : 'text-[#a0a0a0]'}`}
            data-testid="compact-toggle"
          >
            {compact ? 'Compact' : 'Full'} View
          </Button>

          {/* Legend */}
          <div className="flex gap-3 ml-auto">
            {Object.entries(FLAG_CONFIG).map(([key, cfg]) => {
              const Icon = cfg.icon;
              return (
                <div key={key} className="flex items-center gap-1.5">
                  <Icon className={`h-3 w-3 ${cfg.iconColor}`} />
                  <span className="text-xs text-[#a0a0a0]">{cfg.label}</span>
                </div>
              );
            })}
            <div className="flex items-center gap-1.5">
              <div className="h-3 w-3 rounded-sm" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #2a2a2a 0, #2a2a2a 1px, transparent 0, transparent 50%)', backgroundSize: '4px 4px' }} />
              <span className="text-xs text-[#a0a0a0]">Not in scene</span>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto p-4" ref={tableRef}>
        {filteredChars.length === 0 ? (
          <div className="text-center py-12 text-[#a0a0a0]">No characters match the current filter.</div>
        ) : (
          <table className="border-collapse text-sm" data-testid="continuity-grid">
            <thead>
              <tr>
                <th className="sticky left-0 z-20 bg-[#0d0d0d] border border-[#2a2a2a] p-3 text-left min-w-[160px]">
                  <span className="text-xs font-semibold text-[#a0a0a0] uppercase tracking-widest">Character</span>
                </th>
                {data.scenes.map(scene => (
                  <SceneHeader key={scene.id} scene={scene} />
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredChars.map(char => (
                <tr key={char} className="hover:bg-[#1a1a1a]/30 transition-colors">
                  <CharacterHeader char={char} summary={data.summary} search={search} />
                  {data.scenes.map(scene => {
                    const cell = data.grid[char]?.[scene.id];
                    return (
                      <ContinuityCell
                        key={scene.id}
                        cell={cell}
                        onAdd={() => handleCellClick(char, scene.id)}
                        token={token}
                        compact={compact}
                      />
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Cell detail dialog */}
      {selectedCell && (
        <Dialog open onOpenChange={() => setSelectedCell(null)}>
          <DialogContent className="bg-[#1a1a1a] border-[#2a2a2a] text-[#f0f0f0] max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-['DM_Serif_Display'] text-xl">
                {selectedCell.char}
                <span className="text-[#a0a0a0] text-sm font-normal ml-3">
                  Scene {selectedCell.sceneMeta?.scene_number} — {selectedCell.sceneMeta?.location_name}
                </span>
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4">
              {/* Current costumes */}
              {selectedCell.cell?.costumes?.length > 0 ? (
                <div>
                  <div className="text-xs text-[#a0a0a0] mb-2 font-medium uppercase tracking-widest">Assigned Costumes</div>
                  <div className="space-y-2">
                    {selectedCell.cell.costumes.map(item => (
                      <div key={item.id} className="flex items-center gap-3 bg-[#0d0d0d] rounded-lg p-3" data-testid={`dialog-costume-${item.id}`}>
                        <div className="h-10 w-10 rounded bg-[#2a2a2a] flex items-center justify-center shrink-0 overflow-hidden">
                          {item.image_paths?.length > 0 ? (
                            <LightboxThumb paths={item.image_paths} className="h-10 w-10 object-cover" />
                          ) : <span className="text-[#444] text-xs">C</span>}
                        </div>
                        <div className="flex-1 min-w-0">
                          <MLText as="div" className="text-sm text-[#f0f0f0] font-medium">{getBreakdownItemLabel(item.description, 'original')}</MLText>
                          {item.source && <div className="text-xs text-[#a0a0a0] capitalize">{item.source}</div>}
                          {item.notes && <div className="text-xs text-[#555]">{item.notes}</div>}
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCostume(item.id)} className="h-6 w-6 p-0 text-[#a0a0a0] hover:text-red-400" data-testid={`remove-costume-${item.id}`}>
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/20 rounded-lg p-3">
                  <HelpCircle className="h-4 w-4 text-red-400 shrink-0" />
                  <p className="text-sm text-red-300">No costume assigned for <strong>{selectedCell.char}</strong> in this scene.</p>
                </div>
              )}

              {/* Continuity flag info */}
              {selectedCell.cell?.flag === 'changed' && (
                <div className="flex items-start gap-2 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">
                  <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-300 font-medium">Costume changed from previous scene</p>
                    <p className="text-xs text-amber-300/70 mt-0.5">Verify this is an intentional costume change and not a continuity error.</p>
                  </div>
                </div>
              )}

              <div className="border-t border-[#2a2a2a] pt-4">
                <div className="text-xs text-[#a0a0a0] mb-3 font-medium uppercase tracking-widest">Add Costume</div>
                <div className="space-y-3">
                  <Input
                    value={newCostume.description}
                    onChange={e => setNewCostume(p => ({ ...p, description: e.target.value }))}
                    placeholder="e.g. Worn leather jacket with badge"
                    className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] text-sm"
                    data-testid="new-costume-input"
                  />
                  <div className="grid grid-cols-2 gap-3">
                    <Select value={newCostume.source} onValueChange={v => setNewCostume(p => ({ ...p, source: v }))}>
                      <SelectTrigger className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-8 text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#1a1a1a] border-[#2a2a2a]">
                        {['purchase','rent','make','borrow','other'].map(s => <SelectItem key={s} value={s} className="text-[#f0f0f0] capitalize">{s}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      value={newCostume.notes}
                      onChange={e => setNewCostume(p => ({ ...p, notes: e.target.value }))}
                      placeholder="Notes (optional)"
                      className="bg-[#0d0d0d] border-[#2a2a2a] text-[#f0f0f0] h-8 text-sm"
                    />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button variant="outline" onClick={() => setSelectedCell(null)} className="border-[#2a2a2a] text-[#f0f0f0]">Cancel</Button>
                    <Button onClick={handleAddCostume} disabled={savingCostume} className="bg-[#e8c547] hover:bg-[#d6b539] text-black" data-testid="save-costume-btn">
                      <Plus className="h-4 w-4 mr-1" />{savingCostume ? 'Saving...' : 'Add Costume'}
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
